/**
 * The comparison hub.
 *
 * v1 published the six leaves with no parent, so nothing on the site said they
 * were a set and a crawler found them only from the sitemap. This is the URL to
 * link when the answer is "we have a page for that", and it is the page that
 * makes the six of them a section rather than a pile.
 *
 * Each row leads with the rival's name and carries the "choose Fazly if" line,
 * so the list answers the question before the click for the reader who only
 * wanted to know whether it was worth one.
 */

import type { ContentLang } from "@fazly/shared/contentLang";
import { Link } from "@tanstack/react-router";

import { COMPARE_SLUGS, comparePath } from "../../lib/compare/index";
import type { CompareCopy, CompareSlug } from "../../lib/compare/types";
import type { CompareUi } from "../../lib/compare/ui";
import { toolsIndexPath } from "../../lib/tools/index";
import { ContentFooter } from "../ContentFooter";

export function CompareIndex({
  lang,
  ui,
  copy,
}: {
  lang: ContentLang;
  ui: CompareUi;
  copy: Record<CompareSlug, CompareCopy>;
}) {
  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-12 sm:py-16">
      <h1 className="font-display text-[clamp(2rem,7vw,3rem)] leading-[1.1] font-bold tracking-tight text-ink">
        {ui.indexH1}
      </h1>
      <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink2">
        {ui.indexStandfirst}
      </p>

      <ul className="mt-10 divide-y divide-hair border-y-[2.5px] border-ink">
        {COMPARE_SLUGS.map((slug) => (
          <li key={slug}>
            <Link
              to={comparePath(slug, lang)}
              className="group flex flex-col gap-1 py-5 transition-colors hover:bg-inset"
            >
              <span className="font-display text-xl font-bold text-ink group-hover:text-brand">
                {copy[slug].h1}
              </span>
              <span className="text-[16px] leading-relaxed text-ink2">
                {copy[slug].bestForFazly}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <p className="mt-8 text-[17px] leading-relaxed text-body">
        {ui.tryFirst}
      </p>
      <Link
        to={toolsIndexPath(lang)}
        className="rim mt-4 inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
      >
        {ui.toolsCta}
      </Link>

      <ContentFooter lang={lang} />
    </main>
  );
}
